import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, Eye, Home, FileText, Layout, ChevronLeft, ChevronRight, X } from 'lucide-react';

const AdminPreviewPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Only show in development/preview mode - NOT on production/deployed site
  const isProduction = process.env.NODE_ENV === 'production' && !window.location.hostname.includes('preview');
  if (isProduction) {
    return null;
  }

  // Don't show on admin page itself
  if (location.pathname === '/admin-bookings') {
    return null;
  }

  const previewLinks = [
    { name: 'Admin Dashboard', path: '/admin-bookings', icon: Settings, description: 'View bookings & marketing' },
    { name: 'Landing Page', path: '/', icon: Home, description: 'Main public homepage' },
    { name: 'PriceHub / Booking', path: '/book', icon: FileText, description: 'Customer booking flow' },
    { name: 'Login Page', path: '/login', icon: Layout, description: 'Customer login page' },
  ];

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-[#1a4d2e] text-white p-3 rounded-l-lg shadow-lg hover:bg-[#2d5a3d] transition-all"
        title="Open Admin Preview Panel"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    );
  }

  return (
    <>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-4 top-4 z-50 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#2d5a3d] transition-all flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          <span className="text-sm font-medium">Admin Preview</span>
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          {/* Header */}
          <div className="bg-[#1a4d2e] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span className="font-bold">Admin Preview</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 hover:bg-white/20 rounded"
                title="Minimize"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="p-4 bg-[#fff8f0] border-b border-[#ff6b00]/20">
            <p className="text-sm text-[#8d6e63]">
              <strong className="text-[#1a4d2e]">Review changes before deploying.</strong><br />
              Click any section below to preview.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-auto p-4">
            <div className="space-y-2">
              {previewLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                
                return (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className={`w-full text-left p-3 rounded-lg transition-all flex items-start gap-3 ${
                      isActive 
                        ? 'bg-[#1a4d2e] text-white' 
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mt-0.5 ${isActive ? 'text-[#ff6b00]' : 'text-[#1a4d2e]'}`} />
                    <div>
                      <div className={`font-medium ${isActive ? 'text-white' : 'text-gray-800'}`}>
                        {link.name}
                      </div>
                      <div className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                        {link.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/admin-bookings')}
                  className="w-full p-3 bg-[#ff6b00] text-white rounded-lg font-medium hover:bg-[#ff6b00]/90 transition-all flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Open Admin Dashboard
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full p-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-all"
                >
                  🔄 Refresh Page
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              This panel is for preview only.<br />
              Changes are live in the app.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPreviewPanel;
